/* eslint-disable react/no-multi-comp */
/* eslint-disable react/no-unknown-property */
import React, { useCallback, useEffect, useRef, useState } from "react"
import { gsap, Power0, Power1, Power2 } from "gsap"
import { DrawSVGPlugin } from "gsap/DrawSVGPlugin"
import { MotionPathPlugin } from "gsap/MotionPathPlugin"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { ScrollToPlugin } from "gsap/ScrollToPlugin"
import { SplitText } from "gsap/SplitText"
import { css } from "@jsUtils"
import { Headline } from "@shared"
import Step1StartImg from "@assets/images/workflow/Animation_Part_01_frame0.png"
import Step2StartImg from "@assets/images/workflow/Animation_Part_02_frame0.png"
import Step3StartImg from "@assets/images/workflow/Animation_Part_03_frame0.png"
import Step4StartImg from "@assets/images/workflow/Animation_Part_04_frame0.png"
import { PageSection } from "../PageSection"
import Styles from "./workflow.module.scss"
import {
  animationBaseDuration,
  describeArc,
  getTargetScrollValues,
  setSpriteOnCanvas,
  lockPageScroll,
  unlockPageScroll,
  FPS_INTERVAL,
  SPRITE_PATHS,
  SPRITE_SETTINGS,
  TRIGGER_POINTS
} from "./WorkflowUtils"

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger, DrawSVGPlugin, MotionPathPlugin, ScrollToPlugin, SplitText)
}

let scrollTriggerSteps
let scrollTriggerHeader
let ignoreScrollStepActivation = false

const Workflow = props => {
  const { theme, content } = props
  const [ready, setReady] = useState(false)
  const containerEl = useRef(null)
  const perimeterEl = useRef(null)
  const perimeterProgressEl = useRef(null)
  const perimeterCursorEl = useRef(null)
  const introTextEl = useRef(null)
  const step1El = useRef(null)
  const step2El = useRef(null)
  const step3El = useRef(null)
  const step4El = useRef(null)
  const [activeStep, setActiveStep] = useState(0)

  const containerInViewRef = useRef()

  const stepIconSprites = [useRef(null), useRef(null), useRef(null), useRef(null)]
  const stepIconCanvases = [useRef(null), useRef(null), useRef(null), useRef(null)]
  const stepAnimationRefs = [React.useRef(), React.useRef(), React.useRef(), React.useRef()]
  const stepAnimationStartTimeRefs = [
    React.useRef(),
    React.useRef(),
    React.useRef(),
    React.useRef()
  ]
  const stepAnimationPrevTimeRefs = [React.useRef(), React.useRef(), React.useRef(), React.useRef()]
  const stepAnimationFrameRefs = [React.useRef(), React.useRef(), React.useRef(), React.useRef()]

  const hideHeader = () => {
    document.querySelector("body").classList.add("force-hide-header")
    containerInViewRef.current = true
  }
  const showHeader = () => {
    document.querySelector("body").classList.remove("force-hide-header")
    containerInViewRef.current = false
  }

  const animateStep = stepIndex => () => {
    lockPageScroll()
    if (!stepAnimationStartTimeRefs[stepIndex].current) {
      stepAnimationStartTimeRefs[stepIndex].current = Date.now()
    }
    if (!stepAnimationPrevTimeRefs[stepIndex].current) {
      stepAnimationPrevTimeRefs[stepIndex].current = Date.now()
    }
    const elapsed = Date.now() - stepAnimationPrevTimeRefs[stepIndex].current

    if (!stepAnimationFrameRefs[stepIndex].current) {
      stepAnimationFrameRefs[stepIndex].current = 0
    }
    if (elapsed > FPS_INTERVAL) {
      stepAnimationPrevTimeRefs[stepIndex].current = Date.now()
      stepAnimationFrameRefs[stepIndex].current += 1
    }

    if (stepAnimationFrameRefs[stepIndex].current > SPRITE_SETTINGS[stepIndex].totalFrames - 1) {
      window.cancelAnimationFrame(stepAnimationFrameRefs[stepIndex].current)
      unlockPageScroll()
    } else {
      setSpriteOnCanvas({
        context: stepIconCanvases[stepIndex].current.getContext("2d"),
        targetFrameIndex: stepAnimationFrameRefs[stepIndex].current,
        spriteImg: stepIconSprites[stepIndex].current,
        spriteSettings: SPRITE_SETTINGS[stepIndex]
      })
      stepAnimationRefs[stepIndex].current = window.requestAnimationFrame(animateStep(stepIndex))
    }
  }

  const startAnimateStep = stepIndex => {
    // Do nothing if animation already running
    if (!stepAnimationRefs[stepIndex].current) {
      stepAnimationRefs[stepIndex].current = window.requestAnimationFrame(animateStep(stepIndex))
    }
  }
  const resetAnimationStep = stepIndex => {
    window.cancelAnimationFrame(stepAnimationFrameRefs[stepIndex].current)
    stepAnimationRefs[stepIndex].current = undefined
    stepAnimationFrameRefs[stepIndex].current = undefined
    stepAnimationStartTimeRefs[stepIndex].current = undefined
    stepAnimationPrevTimeRefs[stepIndex].current = undefined
  }

  const onSpriteLoaded = stepIndex => {
    setSpriteOnCanvas({
      context: stepIconCanvases[stepIndex].current.getContext("2d"),
      targetFrameIndex: 0,
      spriteImg: stepIconSprites[stepIndex].current,
      spriteSettings: SPRITE_SETTINGS[stepIndex]
    })
  }

  const scrollToStep = stepNumber => {
    setActiveStep(stepNumber)
    if (scrollTriggerSteps) {
      ignoreScrollStepActivation = true
      const scrollTarget = getTargetScrollValues(
        activeStep,
        stepNumber,
        scrollTriggerSteps.start,
        scrollTriggerSteps.end
      )
      for (let i = 0; i < stepNumber; i++) {
        startAnimateStep(i)
      }
      for (let i = stepNumber; i < 4; i++) {
        resetAnimationStep(i)
      }
      gsap.to(window, {
        ...scrollTarget,
        onComplete: () => {
          ignoreScrollStepActivation = false
        }
      })
    }
  }

  const setContainerRefs = useCallback(
    node => {
      containerEl.current = node
      // containerInViewRef(node)
    },
    [containerInViewRef]
  )

  useEffect(() => {
    // Scroll-lock animations as user scrolls within the module
    const timeline = gsap.timeline()
    timeline.addLabel("start")
    timeline.fromTo(
      perimeterProgressEl.current,
      {
        drawSVG: 0
      },
      {
        drawSVG: "0 100%",
        ease: Power0.easeNone
      },
      "start"
    )
    timeline.to(
      perimeterCursorEl.current,
      {
        motionPath: {
          path: "#perimeterCursorPath",
          align: "#perimeterCursorPath",
          alignOrigin: [0.5, 0.5],
          start: 1,
          end: 0
        },
        ease: Power0.easeNone
      },
      "start"
    )

    scrollTriggerSteps = ScrollTrigger.create({
      trigger: containerEl.current,
      animation: timeline,
      start: "top top",
      end: "+=2000",
      pin: true,
      scrub: 0.3,
      onEnter: hideHeader,
      onEnterBack: hideHeader,
      onLeave: showHeader,
      onLeaveBack: showHeader,
      onUpdate: scrollData => {
        if (ignoreScrollStepActivation || !containerInViewRef.current) {
          return
        }
        let farthestStep = 0

        // const startArc = 360 * scrollData.progress
        // document
        //   .getElementById("perimeterCursorTail")
        //   .setAttribute("d", describeArc(500, 500, 375, startArc - 22, startArc))

        if (scrollData.progress >= TRIGGER_POINTS[1]) {
          farthestStep = 1
          startAnimateStep(0)
        } else {
          resetAnimationStep(0)
        }
        if (scrollData.progress >= TRIGGER_POINTS[2]) {
          farthestStep = 2
          startAnimateStep(1)
        } else {
          resetAnimationStep(1)
        }
        if (scrollData.progress >= TRIGGER_POINTS[3]) {
          farthestStep = 3
          startAnimateStep(2)
        } else {
          resetAnimationStep(2)
        }
        if (scrollData.progress >= TRIGGER_POINTS[4]) {
          farthestStep = 4
          startAnimateStep(3)
        } else {
          resetAnimationStep(3)
        }
        if (scrollData.progress >= TRIGGER_POINTS[5]) {
          farthestStep = 5
        }
        setActiveStep(farthestStep)
      }
    })

    return () => {
      if (scrollTriggerSteps) {
        scrollTriggerSteps.kill()
      }
      if (scrollTriggerHeader) {
        scrollTriggerHeader.kill()
      }
    }
  }, [])

  useEffect(() => {
    // "Appear" animations as user scrolls the module into place
    ScrollTrigger.create({
      trigger: perimeterEl.current,
      animation: gsap.timeline().fromTo(
        perimeterEl.current,
        {
          drawSVG: 0
        },
        {
          drawSVG: "0 100%",
          ease: Power1.easeNone
        }
      ),
      start: "top bottom-=150",
      end: "top top+=100",
      scrub: 0.3,
      once: true
    })

    ScrollTrigger.create({
      trigger: step1El.current,
      animation: gsap.from([step1El.current], {
        opacity: 0,
        scaleX: 0,
        scaleY: 0,
        duration: animationBaseDuration,
        ease: Power2.easeOut
      }),
      start: "bottom bottom-=100",
      once: true
    })

    ScrollTrigger.create({
      trigger: step2El.current,
      animation: gsap.from(step2El.current, {
        opacity: 0,
        scaleX: 0,
        scaleY: 0,
        duration: animationBaseDuration,
        ease: Power2.easeOut
      }),
      start: "bottom bottom-=50",
      once: true
    })

    ScrollTrigger.create({
      trigger: step3El.current,
      animation: gsap.from([step3El.current, perimeterCursorEl.current], {
        opacity: 0,
        scaleX: 0,
        scaleY: 0,
        duration: animationBaseDuration,
        ease: Power2.easeOut
      }),
      start: "top bottom",
      once: true
    })

    ScrollTrigger.create({
      trigger: step4El.current,
      animation: gsap.from(step4El.current, {
        opacity: 0,
        scaleX: 0,
        scaleY: 0,
        duration: animationBaseDuration,
        ease: Power2.easeOut,
        delay: animationBaseDuration / 2
      }),
      start: "bottom bottom-=50",
      once: true
    })

    const introTextLine1 = introTextEl.current.querySelector(".line1")
    const introTextLine2Split = new SplitText(introTextEl.current.querySelector(".line2"), {
      type: "chars"
    })
    const introTextTimeline = gsap.timeline()
    introTextTimeline.from([introTextLine1, ...introTextLine2Split.chars], {
      opacity: 0,
      y: 100,
      stagger: animationBaseDuration / 10,
      duration: animationBaseDuration,
      ease: Power1.easeOut,
      delay: animationBaseDuration
    })
    ScrollTrigger.create({
      trigger: step2El.current,
      animation: introTextTimeline,
      start: "bottom bottom-=50",
      once: true
    })

    return () => {}
  }, [])
  const contentConclusion = content[4].headline.split("|")
  return (
    <PageSection
      className={css(Styles.section, ready && Styles.ready)}
      theme={theme}
      ready={ready}
      onReady={() => {
        setReady(true)
      }}
    >
      <div style={{ display: "none" }}>
        {/* Preload sprites */}
        {SPRITE_PATHS.map((spritePath, index) => (
          <img
            key={spritePath}
            src={spritePath}
            onLoad={() => onSpriteLoaded(index)}
            alt=""
            ref={stepIconSprites[index]}
          />
        ))}
      </div>
      <div
        className={css(Styles.wrapper, Styles[`activeStep_${activeStep}`])}
        ref={setContainerRefs}
      >
        <div className={css(Styles.backgroundCircles)}>
          <div className={Styles.circle3} />
          <div className={Styles.circle2} />
          <div className={Styles.circle1} />
        </div>
        <svg
          className={Styles.perimeterContainer}
          viewBox="0 0 1000 1000"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient id="cursorGradient">
              <stop offset="0%" stopColor="rgb(102,105,210)" />
              <stop offset="100%" stopColor="rgb(192,192,234)" />
            </linearGradient>
          </defs>

          <circle ref={perimeterEl} className={Styles.perimeter} cx="500" cy="500" r="375" />
          <circle
            ref={perimeterProgressEl}
            className={Styles.perimeterProgress}
            stroke="url(#cursorGradient)"
            cx="500"
            cy="500"
            r="375"
          />
          <path
            id="perimeterCursorPath"
            fill="none"
            d="
            M 500,125
            a 375,375 0 1,0 0,750
            a 375,375 0 1,0 0,-750"
          />
          <path
            id="perimeterCursorTail"
            stroke="url(#cursorGradient)"
            strokeWidth="6"
            fill="none"
            d=""
          />
          <circle ref={perimeterCursorEl} className={Styles.perimeterCursor} r="6" />
        </svg>
        <div className={Styles.stepIndicatorsContainer}>
          <button
            className={css(
              Styles.stepIndicator,
              Styles.stepIndicator1,
              activeStep >= 1 && Styles.stepActive
            )}
            ref={step1El}
            onClick={() => {
              scrollToStep(1)
            }}
            type="button"
          >
            <div className={css(Styles.stepIndicatorCircle, "stepIndicatorCircle")} />
            <div className={css(Styles.stepContent)}>
              <img src={Step1StartImg} alt="" className={Styles.iconStartImg} />
              <canvas
                className={Styles.iconCanvas}
                ref={stepIconCanvases[0]}
                width="300"
                height="300"
              />
              <div className={Styles.stepLabel}>Ultrasound</div>
            </div>
          </button>
          <button
            className={css(
              Styles.stepIndicator,
              Styles.stepIndicator2,
              activeStep >= 2 && Styles.stepActive
            )}
            ref={step2El}
            onClick={() => {
              scrollToStep(2)
            }}
            type="button"
          >
            <div className={css(Styles.stepIndicatorCircle, "stepIndicatorCircle")} />
            <div className={css(Styles.stepContent)}>
              <img src={Step2StartImg} alt="" className={Styles.iconStartImg} />
              <canvas
                className={Styles.iconCanvas}
                ref={stepIconCanvases[1]}
                width="300"
                height="300"
              />
              <div className={Styles.stepLabel}>Any Device</div>
            </div>
          </button>
          <button
            className={css(
              Styles.stepIndicator,
              Styles.stepIndicator3,
              activeStep >= 3 && Styles.stepActive
            )}
            ref={step3El}
            onClick={() => {
              scrollToStep(3)
            }}
            type="button"
          >
            <div className={css(Styles.stepIndicatorCircle, "stepIndicatorCircle")} />
            <div className={css(Styles.stepContent)}>
              <img src={Step3StartImg} alt="" className={Styles.iconStartImg} />
              <canvas
                className={Styles.iconCanvas}
                ref={stepIconCanvases[2]}
                width="300"
                height="300"
              />
              <div className={Styles.stepLabel}>Cloud</div>
            </div>
          </button>
          <button
            className={css(
              Styles.stepIndicator,
              Styles.stepIndicator4,
              activeStep >= 4 && Styles.stepActive
            )}
            ref={step4El}
            onClick={() => {
              scrollToStep(4)
            }}
            type="button"
          >
            <div className={css(Styles.stepIndicatorCircle, "stepIndicatorCircle")} />
            <div className={css(Styles.stepContent)}>
              <img src={Step4StartImg} alt="" className={Styles.iconStartImg} />
              <canvas
                className={Styles.iconCanvas}
                ref={stepIconCanvases[3]}
                width="300"
                height="300"
              />
              <div className={Styles.stepLabel}>EMR/PACS</div>
            </div>
          </button>
        </div>
        <div className={Styles.interiorText}>
          <div
            className={css(
              Styles.stepInteriorText,
              (activeStep === 0 || activeStep === 5) && Styles.stepInteriorTextActive,
              activeStep > 0 && activeStep < 4 && Styles.stepInteriorTextPast
            )}
            ref={introTextEl}
            datastepnumber={0}
          >
            <Headline className={css("line1", Styles.stepInteriorText0_1)} size={4}>
              {contentConclusion[0]}
            </Headline>
            <Headline className={css("line2", Styles.stepInteriorText0_2)} size={1}>
              {contentConclusion[1]}
            </Headline>
          </div>

          {content
            .filter(element => content.indexOf(element) !== 4)
            .map((item, i) => (
              <div
                className={css(
                  Styles.stepInteriorText,
                  activeStep === i + 1 && Styles.stepInteriorTextActive,
                  activeStep > i + 1 && Styles.stepInteriorTextPast
                )}
                datastepnumber={i + 1}
              >
                <div className={css(Styles.stepInteriorTextEyebrow)}>{item.title}</div>
                <div className={css(Styles.stepInteriorTextTitle)}>{item.headline}</div>
              </div>
            ))}
        </div>
      </div>
    </PageSection>
  )
}

export default Workflow
